import React, {useEffect, useState} from 'react';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {LinearGradient} from 'react-native-linear-gradient';
import {LayoutChangeEvent} from 'react-native';
import {hdp, wdp} from '../../utils/responsive';
import {Theme} from '../../theme/lightTheme';
import Icon from '../icon';
import {isUrl, openUrl} from '../../utils/helpers';
import {AnswerItemProps} from '../../interfaces/components/answerItem';
import {renderArticleContent} from '../../utils/html';
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import AutoHeightWebView from '../../components/autoHeightWebView';

const answerHeight = hdp(25);
const bounceAnimation = hdp(0.3);
const animationDuration = 500;

const Container = styled.View`
  width: 100%;
`;
const AnswerContainer = styled(Animated.View)<{isReferences?: boolean}>`
  width: ${wdp(90)}px;
  align-self: center;
  background-color: ${({theme, isReferences}) =>
    isReferences ? 'transparent' : theme.homeBackground};
  border-radius: 20px;
  overflow: hidden;
`;
const AnswerMeasureContent = styled.View`
  width: ${wdp(90)}px;
  align-self: center;
  position: absolute;
  top: 0;
  opacity: 0;
`;
const CloseAnswerIconContainer = styled.View`
  align-self: flex-end;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 99;
`;
const ExpandAnswerIconContainer = styled.TouchableOpacity`
  height: 100%;
  width: 40%;
  align-self: center;
  align-items: center;
  justify-content: flex-end;
  z-index: 99;
`;
const ExpandAnswerIconAnimatedContainer = styled(Animated.View)`
  height: ${({theme}) => theme.text.s4}px;
  width: ${({theme}) => theme.text.s4}px;
  align-self: center;
  align-items: center;
  position: absolute;
  bottom: ${({theme}) => theme.text.s4 / 3}px;
`;

/**
 * AnswerItem component.
 *
 * @param style
 * @param {AnswerItemProps} props - Props including style customizations and additional TextInput properties.
 * @returns {React.ReactElement} The stylized QuestionInput component with conditional theming and localized placeholders.
 */
const AnswerItem: React.FC<AnswerItemProps> = ({
  style,
  content,
  onClosePressed,
  isReferences,
}: AnswerItemProps): React.ReactElement => {
  const theme = useTheme() as Theme;
  const [showReadeMore, setShowReadeMore] = useState(false);
  const [isExpandAnswerOpen, setIsExpandAnswerOpen] = useState(false);
  const [fullHeight, setFullHeight] = useState(answerHeight);
  const animatedHeight = useSharedValue(answerHeight);
  const translateY = useSharedValue(0);
  const opacity = useSharedValue(1);

  useEffect(() => {
    // Start the first animation
    animatedHeight.value = withTiming(
      isExpandAnswerOpen ? fullHeight + theme.text.s4 + hdp(4) : answerHeight,
      {
        duration: animationDuration,
        easing: Easing.ease,
      },
    );
    // Interpolate opacity based on height animation
    opacity.value = withTiming(
      isExpandAnswerOpen ? 0 : 1,
      {
        duration: 50,
        easing: Easing.ease,
      },
      finished => {
        // When animation finishes, set opacity back to 1
        if (finished) {
          opacity.value = withTiming(1, {
            duration: animationDuration * 2,
            easing: Easing.ease,
          });
        }
      },
    );
    if (isExpandAnswerOpen) {
      cancelAnimation(translateY);
    }
    // Start the second animation
    translateY.value = withRepeat(
      withSequence(
        withTiming(-bounceAnimation, {
          duration: animationDuration,
          easing: Easing.ease,
        }),
        withTiming(bounceAnimation, {
          duration: animationDuration,
          easing: Easing.ease,
        }),
      ),
      -1,
      true,
    );
  }, [isExpandAnswerOpen]);

  useEffect(() => {
    setIsExpandAnswerOpen(false);
  }, [content]);

  const onExpandAnswerPress = () => {
    setIsExpandAnswerOpen(prev => !prev);
  };

  const bounceIconStyle = useAnimatedStyle(() => {
    return {
      transform: [{translateY: translateY.value}],
      opacity: opacity.value,
    };
  });

  const expandStyle = useAnimatedStyle(() => {
    return {
      height: animatedHeight.value,
    };
  });

  return (
    <Container style={style}>
      <AnswerContainer isReferences={isReferences} style={[expandStyle, style]}>
        <LinearGradient
          style={{
            width: '100%',
            height: '100%',
            paddingBottom: isReferences ? hdp(3) : 0,
          }}
          colors={
            !isReferences
              ? ['transparent', 'transparent']
              : [theme.primary + 'DD', theme.secondary + 'DD']
          }>
          {onClosePressed && (
            <CloseAnswerIconContainer>
              <Icon
                name={'close'}
                size={theme.text.s5}
                role={'button'}
                color={isReferences ? theme.text.light : theme.text.grey}
                onPress={onClosePressed}
              />
            </CloseAnswerIconContainer>
          )}
          <MemoizedRenderHtml content={content} />
          {showReadeMore && (
            <LinearGradient
              style={{
                width: '100%',
                height: theme.text.s4 + hdp(4),
                position: isExpandAnswerOpen ? 'relative' : 'absolute',
                bottom: 0,
                alignItems: 'center',
                justifyContent: 'center',
              }}
              isExpandAnswerOpen={isExpandAnswerOpen}
              colors={
                isReferences
                  ? [
                      theme.secondary + '44',
                      theme.secondary + 'EE',
                      theme.secondary,
                    ]
                  : [
                      theme.voiceQuestionInput.background + 'AA',
                      theme.voiceQuestionInput.background,
                      theme.voiceQuestionInput.background,
                    ]
              }>
              <ExpandAnswerIconContainer onPress={onExpandAnswerPress}>
                <ExpandAnswerIconAnimatedContainer style={bounceIconStyle}>
                  <Icon
                    name={isExpandAnswerOpen ? 'arrow-up' : 'arrow-down'}
                    color={
                      isReferences ? theme.text.light : theme.text.grey + 'AA'
                    }
                    size={theme.text.s4}
                  />
                </ExpandAnswerIconAnimatedContainer>
              </ExpandAnswerIconContainer>
            </LinearGradient>
          )}
        </LinearGradient>
      </AnswerContainer>
      <AnswerMeasureContent
        pointerEvents={'none'}
        onLayout={(event: LayoutChangeEvent) => {
          const {height} = event.nativeEvent.layout;
          setFullHeight(height);
          setShowReadeMore(height > answerHeight - (theme.text.s4 + hdp(4)));
        }}>
        <MemoizedRenderHtml content={content} />
      </AnswerMeasureContent>
    </Container>
  );
};

/**
 * Memoized version of the RenderHtml component
 */
export const MemoizedRenderHtml = React.memo(
  ({content}: {content: string}) => {
    const theme = useTheme() as Theme;
    return (
      <AutoHeightWebView
        allowsFullscreenVideo
        style={{
          width: '100%',
          margin: '3%',
        }}
        source={{
          html: renderArticleContent(content, {
            theme,
            isSeeMoreArticle: true,
          }),
        }}
        scrollEnabled
        onShouldStartLoadWithRequest={(event: any) => {
          if (isUrl(event?.url)) {
            openUrl(event.url);
            return false;
          }
          return true;
        }}
        injectedJavaScript="window.ReactNativeWebView.postMessage(document.body.scrollHeight)"
      />
    );
  },
  (prevProps, nextProps) => prevProps.content === nextProps.content,
);

export default AnswerItem;
